import React, { useState } from 'react';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, IconButton, Stack, Typography, alpha,
} from '@mui/material';
import { Close, DeleteOutline, PhotoCamera } from '@mui/icons-material';

import PersonAvatar from '../common/PersonAvatar';
import ConfirmationDialog from '../common/ConfirmationDialog';
import { personPhotoUrl } from '../../utils/personPhoto';
import type { PhotoOwner } from '../../services/personPhotoService';
import { usePersonPhotoActions } from './usePersonPhotoActions';

export interface PersonPhotoDialogProps {
  open: boolean;
  onClose: () => void;
  name?: string | null;
  /** A line under the name, e.g. "GR 000043". */
  caption?: React.ReactNode;
  photoFilename?: string | null;
  /** Where the photo is saved. Omit to only view it. */
  owner?: PhotoOwner;
  /** Offer "Remove photo" (off where a photo is required). Default true. */
  removable?: boolean;
  onChanged?: (photoFilename: string | null) => void;
}

/**
 * A person's photo, large — with Change / Remove for whoever may edit it.
 * Opened from PersonPhoto; usable anywhere a photo needs a closer look.
 */
const PersonPhotoDialog: React.FC<PersonPhotoDialogProps> = ({
  open, onClose, name, caption, photoFilename, owner, removable = true, onChanged,
}) => {
  const { busy, choose, remove, fileInput } = usePersonPhotoActions(owner, onChanged);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const url = personPhotoUrl(photoFilename);

  return (
    <>
      <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
        <Stack direction="row" alignItems="flex-start" sx={{ px: 3, pt: 2.5, pb: 1.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={700} noWrap>{name || 'Photo'}</Typography>
            {caption && <Typography variant="body2" color="text.secondary">{caption}</Typography>}
          </Box>
          <IconButton size="small" onClick={onClose} disabled={busy} aria-label="Close" sx={{ mt: -0.5, mr: -1 }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
        <DialogContent sx={{ pt: 0 }}>
          <Box
            sx={{
              position: 'relative', borderRadius: 2, overflow: 'hidden',
              bgcolor: (t) => alpha(t.palette.text.primary, 0.04),
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              minHeight: 280,
            }}
          >
            {url ? (
              <Box
                component="img"
                src={url}
                alt={name ? `${name}'s photo` : 'Photo'}
                sx={{ display: 'block', width: '100%', maxHeight: '60vh', objectFit: 'contain' }}
              />
            ) : (
              <Stack alignItems="center" spacing={1.5} sx={{ py: 4 }}>
                <PersonAvatar name={name} size={120} />
                <Typography variant="body2" color="text.secondary">No photo yet</Typography>
              </Stack>
            )}
            {busy && (
              <Box sx={{
                position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                bgcolor: (t) => alpha(t.palette.background.paper, 0.6),
              }}>
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
          {owner && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25 }}>
              A clear, recent face photo. JPG or PNG, up to 5 MB. Replacing it deletes the old one.
            </Typography>
          )}
        </DialogContent>
        {owner ? (
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
            {url && removable && (
              <Button
                color="error"
                startIcon={<DeleteOutline />}
                onClick={() => setConfirmRemove(true)}
                disabled={busy}
                sx={{ textTransform: 'none', mr: 'auto' }}
              >
                Remove photo
              </Button>
            )}
            <Button
              variant="contained"
              disableElevation
              startIcon={<PhotoCamera />}
              onClick={choose}
              disabled={busy}
              sx={{ textTransform: 'none' }}
            >
              {url ? 'Change photo' : 'Upload photo'}
            </Button>
          </DialogActions>
        ) : (
          <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
            <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
          </DialogActions>
        )}
        {fileInput}
      </Dialog>

      <ConfirmationDialog
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        onConfirm={async () => {
          setConfirmRemove(false);
          await remove();
        }}
        title="Remove photo?"
        message={`${name || 'This person'}'s photo will be deleted. It comes off their record, the admit card and the printed application.`}
        confirmText="Remove photo"
        confirmColor="error"
      />
    </>
  );
};

export default PersonPhotoDialog;
